import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, X } from "lucide-react";
import { api } from "@/lib/api";
import { to12 } from "@/lib/time";

const DISMISS_KEY = "vv_countdown_dismissed";
const localDay = (d) => d.toLocaleDateString("en-CA");

function startOf(r) {
  const [h, m] = (r.time || "23:59").split(":").map(Number);
  const d = new Date(`${r.date}T00:00:00`);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
}

function left(ms) {
  const mins = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(mins / 60);
  return h ? `${h}h ${mins % 60}m` : `${mins}m`;
}

export function CountdownBanner({ listings, onOpen }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [dismissed, setDismissed] = useState(() => JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]"));
  useEffect(() => { api.get("/reminders/shows/upcoming").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  const today = localDay(now);
  const tomorrow = localDay(new Date(now.getTime() + 86400000));
  const shown = useMemo(() => items.filter((r) => [today, tomorrow].includes(r.date) && startOf(r) > now && !dismissed.includes(r.key)).slice(0, 3),
    [items, today, tomorrow, now, dismissed]);
  if (!shown.length) return null;

  const dismiss = () => {
    const next = [...dismissed, ...shown.map((r) => r.key)].slice(-50);
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    setDismissed(next);
  };
  const open = (r) => {
    const full = listings?.find((l) => l.id === r.listing.id);
    if (full) onOpen(full); else navigate(`/explore?city=${encodeURIComponent(r.city)}`);
  };

  return (
    <div data-testid="countdown-banner" className="rise mb-6 rounded-2xl border border-[#FFB800]/50 bg-[#FFB800]/10 p-3 sm:p-4 flex items-start gap-3">
      <BellRing className="h-5 w-5 text-[#FFB800] shrink-0 mt-0.5" />
      <div className="flex-1 space-y-1.5 min-w-0">
        {shown.map((r, i) => (
          <button key={r.key} type="button" data-testid={`countdown-item-${i}`} onClick={() => open(r)} className="block w-full text-left text-sm hover:text-[#FFB800] transition-colors truncate">
            <b>{r.date === today ? "Today" : "Tomorrow"}</b> · {r.title || r.listing.name}{r.time ? ` at ${to12(r.time)}` : ""}
            <span data-testid={`countdown-left-${i}`} className="ml-2 font-mono-vv text-[#FFB800]">starts in {left(startOf(r) - now)}</span>
          </button>
        ))}
      </div>
      <button type="button" data-testid="countdown-dismiss" aria-label="Dismiss" onClick={dismiss} className="text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
    </div>
  );
}
